import React, { useState } from 'react';
import { Plus, X, Play } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const GameSetup = ({ onGameCreated }) => {
  const [teamNames, setTeamNames] = useState(['', '']);
  const [isCreating, setIsCreating] = useState(false);

  const addTeam = () => {
    setTeamNames([...teamNames, '']);
  };

  const removeTeam = (index) => {
    if (teamNames.length > 2) {
      setTeamNames(teamNames.filter((_, i) => i !== index));
    }
  };

  const updateTeamName = (index, value) => {
    const newNames = [...teamNames];
    newNames[index] = value;
    setTeamNames(newNames);
  };

  const createGame = async () => {
    const validNames = teamNames.filter(name => name.trim() !== '');
    
    if (validNames.length < 2) {
      toast.error('At least 2 teams are required!');
      return;
    }

    setIsCreating(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ team_names: validNames }),
      });

      if (response.ok) {
        const data = await response.json();
        onGameCreated(data.game_id, data.game);
      } else {
        toast.error('Failed to create game');
      }
    } catch (error) {
      console.error('Error creating game:', error);
      toast.error('Failed to create game');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="border-2" data-testid="game-setup-card">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-2 w-2 rounded-full bg-[hsl(var(--secondary))]" />
            <Badge variant="secondary" className="font-[var(--font-mono)] text-xs">
              Setup
            </Badge>
          </div>
          <CardTitle className="text-3xl font-[var(--font-display)] tracking-tight">
            Create New Game
          </CardTitle>
          <CardDescription className="text-base">
            Enter team names to start a new game. Each team will take turns guessing songs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {teamNames.map((name, index) => (
            <div key={index} className="flex gap-2">
              <Input
                placeholder={`Team ${index + 1} Name`}
                value={name}
                onChange={(e) => updateTeamName(index, e.target.value)}
                className="flex-1"
                data-testid={`team-name-input-${index}`}
              />
              {teamNames.length > 2 && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => removeTeam(index)}
                  data-testid={`remove-team-${index}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          <Button
            variant="outline"
            onClick={addTeam}
            className="w-full rounded-xl"
            data-testid="add-team-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Another Team
          </Button>

          <Button
            onClick={createGame}
            disabled={isCreating}
            className="w-full rounded-xl text-lg h-12 shadow-[0_10px_24px_rgba(0,0,0,0.10)]"
            data-testid="start-game-button"
          >
            <Play className="mr-2 h-5 w-5" />
            {isCreating ? 'Creating Game...' : 'Start Game'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default GameSetup;
